import { useRef } from 'react'
import { jwtDecode } from 'jwt-decode'
import { useRequest } from 'ahooks'

import { asyncSuccessResolver, toast } from '$app/utilities'
import { ToastMessage } from '$app/types'

import { errorMessageResolver, successResolver } from '$services/api/common'
import { addToken } from '$store/session'
import { MfaAuthAPI } from '$services/api/mfa-auth'
import { DecodedToken, Token } from '$store/session/tokens'

import { LoginFormProps } from '$hook-forms/LoginForm'

import { AuthAPI } from '$services/api/auth'
import { AnonymousAuthAPI } from '$api/anonymous-auth'
import { parseNo2faGuardStagedContentResolver } from '$api/resolvers'

import { useSuccessLogin } from './useSuccessLogin'
import useMFABlock from './useMFABlock'
import use2FAHelper from './use2FAHelper'
import useCreateAuthRTC from './useCreateAuthRTC'

const toastMsg = {
  loading: 'Authenticating account...',
  success: 'Successfully logged in'
} satisfies ToastMessage

export const useEmailLogin = () => {
  const { current: anonymousApi } = useRef(new AnonymousAuthAPI(null))
  const { current: authAPI } = useRef(new AuthAPI(null))
  const { current: mfaApi } = useRef(new MfaAuthAPI(null))
  const [authRTC, createAuthRTC] = useCreateAuthRTC()

  const setAuthToken: typeof anonymousApi.updateToken = token => {
    anonymousApi.updateToken(token)
    authAPI.updateToken(token)
    mfaApi.updateToken(token)

    if (!token) return

    const { jti: token_id, sub: user_id } = jwtDecode<DecodedToken>(token)

    createAuthRTC({ authAPI, token_id, user_id })
  }

  const { login, login2FA } = anonymousApi

  const onLoginSuccess = useSuccessLogin()

  const loginRequest = useRequest(
    asyncSuccessResolver(login, successResolver),
    { manual: true }
  )
  const login2FARequest = useRequest(
    asyncSuccessResolver(login2FA, successResolver),
    { manual: true }
  )

  const twoFAHelper = use2FAHelper({ getMfaBlock: () => mfaBlock })
  const preferred_factor = twoFAHelper.getPreferredFactorByContent(
    loginRequest.data?.data
  )
  const available_factors = twoFAHelper.getFactorsByContent(
    loginRequest.data?.data
  )

  const mfaBlock = useMFABlock({
    apiInstance: mfaApi,
    authRTC,
    data: { available_factors, preferred_factor },
    onVerified: mfa_token => {
      const { runAsync } = login2FARequest

      toast
        .promise(runAsync({ mfa_token }), toastMsg)
        .unwrap()
        .then(({ data: { auth_token } }) => {
          setAuthToken(null)
          onTokenSuccess(auth_token)
        })
        .catch(twoFAHelper.reChallengeOrExitOnError)
    }
  })

  const onTokenSuccess = (token: Token) => {
    const id = addToken(token)

    onLoginSuccess(id)
  }

  const onFormSubmit: LoginFormProps['onSuccess'] = async (reset, data) => {
    const { runAsync } = loginRequest

    const { data: result } = await toast
      .promise(runAsync(data), {
        ...toastMsg,
        success: ({ data, message }) => {
          return twoFAHelper.is2FAContent(data) ? message : toastMsg.success
        }
      })
      .unwrap()

    reset()

    const is2FAPerformed = twoFAHelper.perform2FAActionByContent(
      result,
      ({ auth_token }) => {
        setAuthToken(auth_token)
        mfaBlock.modalControls.open()
      }
    )

    if (is2FAPerformed) return

    const { auth_token } = { ...parseNo2faGuardStagedContentResolver(result) }

    if (auth_token) {
      onTokenSuccess(result.auth_token)
    }
  }

  const errorMessage = [loginRequest, login2FARequest]
    .map(({ error }) => error)
    .filter(Boolean)
    .map(e => errorMessageResolver(e, 'Invalid credential'))
    .join()

  const loginForm: LoginFormProps = {
    errorMessage,
    onSuccess: onFormSubmit,
    disabled: loginRequest.loading || login2FARequest.loading
  }

  return {
    loginForm,
    onTokenSuccess,
    mfaBlock
  }
}

export default useEmailLogin
